import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Collapse, Modal } from "react-bootstrap";
import Select from "react-select";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  MdAdd,
  MdArrowBack,
  MdArrowForward,
  MdClose,
  MdFileDownload,
  MdKeyboardArrowDown,
  MdRefresh,
  MdSearch,
  MdPeopleOutline,
  MdFilterAlt,
  MdSave,
  MdSend,
} from "react-icons/md";
import { FiRefreshCw, FiExternalLink } from "react-icons/fi";
import { BsThreeDotsVertical } from "react-icons/bs";
import { createPortal } from "react-dom";
import { ResponsiveContainer, ComposedChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar } from "recharts";
import { toast } from "react-toastify";
import {
  Utils,
  fetchWithAuthLogged,
  selectStyles,
  handleSessionExpired,
  fetchApplicationAppId,
  fetchAjaxListData,
  getBrowserTimeZone,
  getDateRangeForPreset,
  DATE_RANGE_PRESETS,
  formatDateTime,
  exportListCsv,
  bigAmountFormat,
  getAuthUserCurrency,
  can,
  labelizeKey,
  dateRangeLabel,
  FUNNEL_STEP_SEPARATOR,
  FUNNEL_MIN_STEPS,
  FUNNEL_MAX_STEPS,
  FUNNEL_USERS_PAGE_SIZE,
  FUNNEL_SEGMENT_USER_CAP,
  FUNNEL_BREAKDOWN_OPTIONS,
  FUNNEL_MODE_CODES,
  FUNNEL_SOURCE_OPTIONS,
  FUNNEL_WINDOW_OPTIONS,
} from "../../utils";
import DateRangePicker from "../common/DateRangePicker/DateRangePicker";
import AsyncSearchSelect from "../common/AsyncSearchSelect";
import HelpTooltip from "../common/HelpTooltip";
import { useActionDropdown } from "../../hooks/useActionDropdown";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import SkeletonLoader from "../common/SkeletonLoader";
import ListPaginationBar from "../common/ListPaginationBar";
import "./engage.css";


const KPI_HELP = {
  started:
    "Users who did the first step during the selected date range. Every other number on this page is counted from these users.",
  completed: "Users who went through every step of the funnel (within the time limit, if one is set).",
  conversion: "Completed ÷ Started × 100. Out of everyone who started, how many finished the whole funnel.",
  drop: "The step-to-step move where the most users were lost. This is usually the first place to improve.",
  time: "Average time users took from the first step to the last step. Only users who completed the funnel are counted. Median is the middle value: half the users were faster, half slower.",
};

/* Saved funnel <-> page state. Filters keep the date PRESET (e.g. last_30_days) so a saved funnel
   always opens on fresh dates; exact dates are stored only for a custom range. */
function filtersToSaved(f) {
  const preset = f.datePreset && f.datePreset !== "custom" ? f.datePreset : "";
  return {
    date_preset: preset || (f.dateFrom && f.dateTo ? "custom" : ""),
    date_from: preset ? "" : f.dateFrom || "",
    date_to: preset ? "" : f.dateTo || "",
    campaign_id: f.campaign_id || "",
    is_organic: f.is_organic === "" || f.is_organic == null ? "" : String(f.is_organic),
    country: f.country || "",
    platform: f.platform || "",
    state: f.state || "",
    city: f.city || "",
    breakdown: f.breakdown || "",
    exclude_events: f.exclude_events || "",
  };
}

function savedToFilters(saved = {}) {
  let f = { ...getDefaultFilters(), dateFrom: "", dateTo: "", datePreset: "" };
  if (saved.date_preset && saved.date_preset !== "custom" && DATE_RANGE_PRESETS.some((x) => x.id === saved.date_preset)) {
    const r = getDateRangeForPreset(saved.date_preset);
    f = { ...f, dateFrom: r.dateFrom, dateTo: r.dateTo, datePreset: r.preset };
  } else if (saved.date_from && saved.date_to) {
    f = { ...f, dateFrom: saved.date_from, dateTo: saved.date_to, datePreset: "custom" };
  } else {
    f = { ...f, ...getDefaultFilters() };
  }
  return {
    ...f,
    campaign_id: saved.campaign_id != null && saved.campaign_id !== "" ? String(saved.campaign_id) : "",
    is_organic: saved.is_organic != null && saved.is_organic !== "" ? String(saved.is_organic) : "",
    country: saved.country || "",
    platform: saved.platform || "",
    state: saved.state || "",
    city: saved.city || "",
    breakdown: saved.breakdown || "",
    exclude_events: saved.exclude_events || "",
  };
}

/** Comparable snapshot of what gets saved — used to show "unsaved changes". */
function configKey({ name, description, steps, mode, windowDays, filters }) {
  return JSON.stringify({
    n: String(name || "").trim(),
    d: String(description || "").trim(),
    s: steps.map((x) => String(x || "").trim()),
    m: mode,
    w: String(windowDays || ""),
    f: filtersToSaved(filters),
  });
}

// Amount (revenue) of an Event Metric step, in the user's currency.
const money = (v) => bigAmountFormat(v, getAuthUserCurrency());

function pct(v) {
  const n = Number(v) || 0;
  return `${Number.isInteger(n) ? n : n.toFixed(1)}%`;
}

const splitStep = (s) => String(s || "").split(FUNNEL_STEP_SEPARATOR).filter(Boolean);

function plural(n, word) {
  return `${bigAmountFormat(n)} ${word}${Number(n) === 1 ? "" : "s"}`;
}

function formatDuration(ms) {
  if (ms == null) return "—";
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec} sec`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min`;
  const hr = min / 60;
  if (hr < 24) return `${Math.round(hr * 10) / 10} hr`;
  const d = Math.round((hr / 24) * 10) / 10;
  return `${d} day${d === 1 ? "" : "s"}`;
}

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "2026-06-08" -> "8 Jun" (days / weeks) or "Jun 2026" (months). Parsed as plain text so no timezone shift. */
function trendLabel(date, unit, withYear = false) {
  const [y, m, d] = String(date).split("-").map(Number);
  if (!y || !m) return String(date);
  if (unit === "month") return `${MONTHS_SHORT[m - 1]} ${y}`;
  return `${d} ${MONTHS_SHORT[m - 1]}${withYear ? ` ${y}` : ""}`;
}

function getDefaultFilters() {
  const range = getDateRangeForPreset("last_7_days");
  return {
    campaign_id: "",
    is_organic: "",
    country: "",
    platform: "",
    state: "",
    city: "",
    breakdown: "",
    exclude_events: "",
    dateFrom: range.dateFrom,
    dateTo: range.dateTo,
    datePreset: range.preset,
  };
}

/* ---------------------------------------------------------------- chart -- */

function FunnelChart({ data, onOpen }) {
  const [hover, setHover] = useState(null);
  const openAndClear = (i, kind) => {
    setHover(null);
    onOpen(i, kind);
  };
  const wrapRef = useRef(null);
  const first = data[0]?.users || 0;
  const ticks = [100, 75, 50, 25, 0];

  const showTip = (e, i, part) => {
    const box = wrapRef.current?.getBoundingClientRect();
    if (!box) return;
    setHover({ i, part, x: e.clientX - box.left, y: e.clientY - box.top, w: box.width });
  };

  return (
    <div className="funnel-chart" ref={wrapRef} onMouseLeave={() => setHover(null)}>
      <div className="funnel-chart-plot">
        <div className="funnel-chart-yaxis" aria-hidden="true">
          {ticks.map((t) => (
            <span key={t} style={{ bottom: `${t}%` }}>{t}%</span>
          ))}
        </div>
        <div className="funnel-chart-grid" aria-hidden="true">
          {ticks.map((t) => (
            <i key={t} style={{ bottom: `${t}%` }} />
          ))}
        </div>
        <div className="funnel-chart-cols" style={{ gridTemplateColumns: `repeat(${data.length}, minmax(0, 1fr))` }}>
          {data.map((d, i) => {
            const h = first ? (d.users / first) * 100 : 0;
            const prevH = i === 0 ? h : first ? (data[i - 1].users / first) * 100 : 0;
            const dropH = Math.max(0, prevH - h);
            return (
              <div className="funnel-col" key={d.event}>
                <div className="funnel-col-track">
                  {dropH > 0 && (
                    <button
                      type="button"
                      className={`funnel-col-drop ${hover?.i === i && hover?.part === "drop" ? "is-hover" : ""}`}
                      style={{ bottom: `${h}%`, height: `${dropH}%` }}
                      aria-label={`${d.dropped} users dropped before ${labelizeKey(d.event)}`}
                      onMouseMove={(e) => showTip(e, i, "drop")}
                      onFocus={(e) => showTip(e, i, "drop")}
                      onClick={() => openAndClear(i - 1, "dropped")}
                    />
                  )}
                  <button
                    type="button"
                    className={`funnel-col-bar ${hover?.i === i && hover?.part === "bar" ? "is-hover" : ""}`}
                    style={{ height: `${Math.max(h, d.users ? 0.8 : 0)}%` }}
                    aria-label={`${d.users} users reached ${labelizeKey(d.event)}`}
                    onMouseMove={(e) => showTip(e, i, "bar")}
                    onFocus={(e) => showTip(e, i, "bar")}
                    onClick={() => d.users && openAndClear(i, "reached")}
                  />
                </div>
                {i > 0 && (
                  <div className="funnel-col-conv" title="Conversion from the previous step">
                    <MdArrowForward />
                    <span>{pct(d.pct_of_prev)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="funnel-chart-labels" style={{ gridTemplateColumns: `repeat(${data.length}, minmax(0, 1fr))` }}>
        {data.map((d, i) => (
          <div key={d.event} className="funnel-chart-label">
            <small>Step {i + 1}</small>
            <strong title={labelizeKey(d.event)}>{labelizeKey(d.event)}</strong>
            <span className="funnel-label-val"><b>{bigAmountFormat(d.users)}</b> · {pct(d.pct_of_first)}</span>
            {d.amount != null && <span className="funnel-label-val"><b>{money(d.amount)}</b>{d.events != null ? ` (${bigAmountFormat(d.events)})` : ""}</span>}
          </div>
        ))}
      </div>
      <div className="funnel-legend">
        <span><i className="funnel-legend-swatch funnel-legend-swatch--reached" /> Reached the step</span>
        <span><i className="funnel-legend-swatch funnel-legend-swatch--drop" /> Dropped off from the previous step</span>
      </div>

      {hover && data[hover.i] && (
        <div
          className="funnel-tip"
          style={{ left: Math.min(Math.max(hover.x + 14, 8), hover.w - 250), top: Math.max(hover.y - 20, 0) }}
        >
          {hover.part === "bar" ? (
            <>
              <div className="funnel-tip-title">Step {hover.i + 1} · {labelizeKey(data[hover.i].event)}</div>
              <div className="funnel-tip-row"><span>Users reached</span><strong>{bigAmountFormat(data[hover.i].users)}</strong></div>
              {data[hover.i].events != null && (
                <div className="funnel-tip-row"><span>Events in range</span><strong>{bigAmountFormat(data[hover.i].events)}</strong></div>
              )}
              {data[hover.i].amount != null && (
                <div className="funnel-tip-row"><span>Amount</span><strong>{money(data[hover.i].amount)}{data[hover.i].events != null ? ` (${bigAmountFormat(data[hover.i].events)})` : ""}</strong></div>
              )}
              <div className="funnel-tip-row"><span>Of step 1 users</span><strong>{pct(data[hover.i].pct_of_first)}</strong></div>
              {hover.i > 0 && (
                <>
                  <div className="funnel-tip-row"><span>From previous step</span><strong>{pct(data[hover.i].pct_of_prev)}</strong></div>
                  {data[hover.i].avg_ms_from_prev != null && (
                    <div className="funnel-tip-row"><span>Avg time from previous</span><strong>{formatDuration(data[hover.i].avg_ms_from_prev)}</strong></div>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <div className="funnel-tip-title">Dropped after {labelizeKey(data[hover.i - 1]?.event)}</div>
              <div className="funnel-tip-row"><span>Users lost</span><strong>{bigAmountFormat(data[hover.i].dropped)}</strong></div>
              <div className="funnel-tip-row">
                <span>Of previous step</span>
                <strong>{pct(100 - (Number(data[hover.i].pct_of_prev) || 0))}</strong>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------- users modal -- */

function FunnelUsersModal({ mode, show, onHide, stepIndex, kind, group, steps, data, rows, meta, loading, page, onPage, onExport, exporting, onCreateSegment }) {
  const stepName = labelizeKey(steps[stepIndex]);
  const nextName = labelizeKey(steps[stepIndex + 1]);
  const amountStep = data[stepIndex]?.amount != null; // Event Metric step that shows an amount

  return (
    <Modal show={show} onHide={onHide} size="xl" className="ns-modal" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          {kind === "dropped" ? `Dropped before ${nextName} · Step ${stepIndex + 2}` : `Step ${stepIndex + 1} · ${stepName}`}{group ? ` · ${group}` : ""}
          <small className="funnel-users-sub">
            {kind === "all"
              ? `Everyone who did ${labelizeKey(steps[0])}, with how far each one got`
              : kind === "reached"
                ? `Users who did ${stepName}${stepIndex > 0 ? (mode === "any" ? " (in any order)" : " after the previous steps") : ""}`
                : `Users who did ${stepName} but never did ${nextName}`}
          </small>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {kind === "dropped" && meta?.next_summary?.length > 0 && (
          <div className="funnel-next-summary">
            <span>After dropping, these users also did:</span>
            {meta.next_summary.map((n) => (
              <span key={n.event} className="badge rounded-pill bg-light-secondary">
                {labelizeKey(n.event)} · {plural(n.users, "user")}
              </span>
            ))}
          </div>
        )}
        {loading ? (
          <SkeletonLoader columns={[1, 2, 3, 4, 5, 6]} rows={5} />
        ) : rows.length === 0 ? (
          <div className="funnel-empty funnel-empty--sm">
            <MdPeopleOutline size={34} />
            <p>No users here for the selected filters.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table funnel-users-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Contact</th>
                  <th>Platform</th>
                  <th>Progress</th>
                  <th>{stepName} at</th>
                  {amountStep && <th className="text-end">{stepName} amount</th>}
                  <th>Last step reached</th>
                  <th>Path (event by event)</th>
                  {kind === "dropped" && <th>Did instead</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => {
                  const display = u.name || (u.is_anonymous ? "Anonymous user" : `User #${u.app_user_id}`);
                  return (
                    <tr key={u.key}>
                      <td>
                        <div className="funnel-user">
                          <span className={`funnel-avatar ${u.is_anonymous ? "funnel-avatar--anon" : ""}`}>{u.is_anonymous ? "?" : initials(display)}</span>
                          <div>
                            <div className="funnel-user-name">{display}</div>
                            <small className="text-muted">
                              {u.is_anonymous ? (u.device ? `Not logged in · ${u.device}` : "Not logged in yet") : `ID ${u.app_user_id}`}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-column">
                          {u.phone && <span>{u.phone}</span>}
                          {u.email && <small className="text-muted">{u.email}</small>}
                          {!u.phone && !u.email && <small className="text-muted">—</small>}
                        </div>
                      </td>
                      <td>
                        {u.platforms?.length ? (
                          u.platforms.map((p) => <span key={p} className="badge rounded-pill bg-light-secondary me-1">{p}</span>)
                        ) : (
                          <small className="text-muted">—</small>
                        )}
                      </td>
                      <td>
                        <div
                          className="funnel-dots"
                          title={steps.map((s, i) => `${labelizeKey(s)}: ${u.step_times[i] ? formatDateTime(u.step_times[i]) : "not yet"}`).join("\n")}
                        >
                          {steps.map((s, i) => (
                            <i key={s} className={i < u.reached_steps ? "on" : ""} />
                          ))}
                          <small>{u.reached_steps}/{steps.length}</small>
                        </div>
                      </td>
                      <td>
                        {u.step_times[stepIndex] ? (
                          <small>{formatDateTime(u.step_times[stepIndex])}</small>
                        ) : kind === "all" ? (
                          <span className="badge rounded-pill bg-light-secondary">Not reached</span>
                        ) : (
                          <small>—</small>
                        )}
                      </td>
                      {amountStep && <td className="text-end">{u.step_times[stepIndex] ? `${money(u.step_amounts?.[stepIndex] ?? 0)}${u.step_counts?.[stepIndex] != null ? ` (${u.step_counts[stepIndex]})` : ""}` : "—"}</td>}
                      <td><small>{labelizeKey(steps[u.reached_steps - 1])}</small></td>
                      <td>
                        {u.path?.length ? (
                          <div className="funnel-path">
                            {u.path.map((n, k) => (
                              <React.Fragment key={`${n.event}-${k}`}>
                                {k > 0 && <span className="funnel-path-arrow">→</span>}
                                <span
                                  className={`funnel-path-chip ${n.step ? "funnel-path-chip--step" : ""}`}
                                  title={formatDateTime(n.at)}
                                >
                                  {labelizeKey(n.event)}{n.count > 1 ? ` ×${n.count}` : ""}
                                </span>
                              </React.Fragment>
                            ))}
                            {u.path_total > u.path.length && <span className="funnel-path-more">+{u.path_total - u.path.length} more</span>}
                          </div>
                        ) : (
                          <small className="text-muted">—</small>
                        )}
                      </td>
                      {kind === "dropped" && (
                        <td>
                          {u.next_events?.length ? (
                            <div className="d-flex flex-column">
                              <span>
                                {u.next_events.map((n) => `${labelizeKey(n.event)}${n.count > 1 ? ` ×${n.count}` : ""}`).join(", ")}
                              </span>
                              {u.last_activity_at && <small className="text-muted">Last active {formatDateTime(u.last_activity_at)}</small>}
                            </div>
                          ) : (
                            <small className="text-muted">No activity since</small>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="funnel-users-footer">
        <div className="flex-grow-1">
          {meta?.total ? (
            <ListPaginationBar
              page={page}
              setPage={onPage}
              total={meta.total}
              pageSize={FUNNEL_USERS_PAGE_SIZE}
              totalPages={meta.totalPages}
              ariaLabel="Funnel users pagination"
              loading={loading}
              className="p-0"
            />
          ) : null}
        </div>
        <div className="d-flex align-items-center gap-2">
          {onCreateSegment && (
            <button type="button" className="btn_theme_secondary" onClick={onCreateSegment} disabled={!meta?.total}>
              <MdSend /> Message these users
            </button>
          )}
          <button type="button" className="btn_theme_secondary" onClick={onExport} disabled={exporting || !meta?.total}>
            <MdFileDownload /> {exporting ? "Exporting…" : "Export CSV"}
          </button>
          <button type="button" className="btn_theme_primary" onClick={onHide}>Close</button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

/* ----------------------------------------------------------------- page -- */

export default function FunnelAnalysis() {
  const { appId: routeAppId, funnelId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !funnelId;
  const canManage = can("funnel.manage");
  const [appUuid, setAppUuid] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [savedKey, setSavedKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [funnelLoading, setFunnelLoading] = useState(!isNew);
  const [funnelMissing, setFunnelMissing] = useState(false);
  const [steps, setSteps] = useState(["", ""]);
  const [mode, setMode] = useState("ordered");
  const [windowDays, setWindowDays] = useState("");
  const [filters, setFilters] = useState(getDefaultFilters);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [result, setResult] = useState(null);
  const [applied, setApplied] = useState(null);
  const [loading, setLoading] = useState(false);

  const stepTableRef = useRef(null);
  const stepTableInnerRef = useRef(null);
  const breakdownTableRef = useRef(null);
  const breakdownTableInnerRef = useRef(null);
  const { openActionId, openActionItem, dropdownPosition, dropdownRef, handleDropdownAction, handleOpenDropdown } = useActionDropdown({
    tableScrollRef: stepTableRef,
    optionCount: 1,
    menuWidth: 200,
    useAnchored: true,
  });
  const [usersModal, setUsersModal] = useState({ show: false, stepIndex: 0, kind: "reached" });
  const [usersData, setUsersData] = useState({ rows: [], meta: {} });
  const [usersPage, setUsersPage] = useState(1);
  const [usersLoading, setUsersLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const loadedFor = useRef(null);
  // "Message these users": turns the users of the open list into a Segment, then offers where to use it.
  const [segmentModal, setSegmentModal] = useState({ show: false, name: "", saving: false, created: null });

  const fetchOpts = useCallback(
    (type, extra = {}) =>
      ({ search, ids }) =>
        fetchAjaxListData({ type, q: search, id: ids, extra: { app_id: appUuid, ...extra }, mode: "options" }),
    [appUuid]
  );
  // with_metrics: also list the app's Event Metrics (Booking, Revenue…) — a step can be one of them.
  const fetchEventOpts = useMemo(() => fetchOpts("campaign-event", { with_metrics: 1 }), [fetchOpts]);
  const fetchCampaignOpts = useMemo(() => fetchOpts("campaigns"), [fetchOpts]);
  const fetchCountryOpts = useMemo(() => fetchOpts("campaign-country"), [fetchOpts]);
  // Events already used in another step are not offered again (each step must be different).
  const stepOptsFor = useCallback(
    (i) => async (args) => {
      const res = await fetchEventOpts(args);
      if (args?.ids?.length) return res;
      const taken = new Set(steps.filter((x, k) => k !== i && x).flatMap(splitStep));
      return Array.isArray(res) ? res.filter((o) => !taken.has(String(o?.value))) : res;
    },
    [fetchEventOpts, steps]
  );
  // Exclusion list: plain events only (not Event Metrics), and never one that is already a funnel step.
  const fetchExcludeOpts = useCallback(
    async (args) => {
      const res = await fetchEventOpts(args);
      if (args?.ids?.length) return res;
      const taken = new Set(steps.filter(Boolean).flatMap(splitStep));
      return Array.isArray(res) ? res.filter((o) => !String(o?.value).startsWith("metric:") && !taken.has(String(o?.value))) : res;
    },
    [fetchEventOpts, steps]
  );
  const fetchPlatformOpts = useMemo(() => fetchOpts("subscription-platform"), [fetchOpts]);
  const fetchStateOpts = useMemo(() => fetchOpts("app-user-state"), [fetchOpts]);
  const fetchCityOpts = useMemo(() => fetchOpts("app-user-city"), [fetchOpts]);

  const cleanSteps = steps.map((s) => String(s || "").trim()).filter(Boolean);
  const allParts = cleanSteps.flatMap(splitStep);
  const duplicate = new Set(allParts).size !== allParts.length;
  // an event already used in another step marks that step as a duplicate
  const isDupStep = (s, i) => splitStep(s).some((p) => steps.some((x, k) => k !== i && splitStep(x).includes(p)));
  const canRun = cleanSteps.length >= FUNNEL_MIN_STEPS && cleanSteps.length === steps.length && !duplicate;

  const runWith = useCallback(
    async (st, md, wd, f, { silent = false } = {}) => {
      if (!appUuid) return;
      const body = {
        app_id: appUuid,
        steps: st,
        mode: md,
        window_days: wd || undefined,
        campaign_id: f.campaign_id || undefined,
        is_organic: f.is_organic === "" ? undefined : f.is_organic,
        country: f.country || undefined,
        platform: f.platform || undefined,
        state: f.state || undefined,
        city: f.city || undefined,
        breakdown: f.breakdown || undefined,
        exclude_events: f.exclude_events || undefined,
        ...(f.dateFrom && f.dateTo
          ? { date_from: f.dateFrom, date_to: f.dateTo, timezone: getBrowserTimeZone() || undefined }
          : {}),
      };
      setLoading(true);
      let res;
      let json;
      try {
        res = await fetchWithAuthLogged("funnel", `${Utils.baseurl}get/campaign/funnel`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        json = await res.json().catch(() => ({}));
      } catch {
        if (!silent) toast.error("Could not reach the server. Please try again.");
        return;
      } finally {
        setLoading(false);
      }
      if (handleSessionExpired(res, json)) return;
      if (!json.status) {
        if (!silent) {
          const timedOut = res.status === 504 || res.status === 503 || res.status === 502;
          toast.error(
            json.message ||
              (timedOut
                ? "The server took too long to build this funnel. Try a shorter date range or add a filter."
                : "Failed to load funnel")
          );
        }
        return;
      }
      setResult(json);
      setApplied({ body, filters: f, mode: md, windowDays: wd });
    },
    [appUuid]
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      const uuid = await fetchApplicationAppId(routeAppId);
      if (alive && uuid) setAppUuid(uuid);
    })();
    return () => {
      alive = false;
    };
  }, [routeAppId]);

  // View page: load the saved funnel, apply its saved filters and build the report straight away.
  useEffect(() => {
    if (!appUuid) return;
    const key = `${appUuid}:${funnelId || "new"}`;
    if (loadedFor.current === key) return;
    loadedFor.current = key;
    if (isNew) {
      setFunnelLoading(false);
      setFunnelMissing(false);
      setName("");
      setDescription("");
      setSteps(["", ""]);
      setMode("ordered");
      setWindowDays("");
      setFilters(getDefaultFilters());
      setResult(null);
      setApplied(null);
      setSavedKey(null);
      return;
    }
    (async () => {
      setFunnelLoading(true);
      setFunnelMissing(false);
      const res = await fetchWithAuthLogged("funnel", `${Utils.baseurl}get/funnel/detail`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ app_id: appUuid, id: funnelId }),
      });
      const json = await res.json().catch(() => ({}));
      setFunnelLoading(false);
      if (handleSessionExpired(res, json)) return;
      if (!json.status || !json.data) {
        setFunnelMissing(true);
        toast.error(json.message || "Funnel not found");
        return;
      }
      const d = json.data;
      const st = Array.isArray(d.steps) && d.steps.length >= FUNNEL_MIN_STEPS ? d.steps : ["", ""];
      const md = FUNNEL_MODE_CODES.some((m) => m.key === d.mode) ? d.mode : "ordered";
      const wd = d.window_days ? String(d.window_days) : "";
      const f = savedToFilters(d.filters || {});
      setName(d.name || "");
      setDescription(d.description || "");
      setSteps(st);
      setMode(md);
      setWindowDays(wd);
      setFilters(f);
      setFiltersOpen(Boolean(f.campaign_id || f.is_organic !== "" || f.country || f.platform || f.state || f.city));
      setSavedKey(configKey({ name: d.name, description: d.description, steps: st, mode: md, windowDays: wd, filters: f }));
      runWith(st, md, wd, f, { silent: true });
    })();
  }, [appUuid, funnelId, isNew, runWith]);

  const currentKey = configKey({ name, description, steps, mode, windowDays, filters });
  const isDirty = isNew ? Boolean(name.trim() || cleanSteps.length) : savedKey != null && currentKey !== savedKey;

  const goBack = () => {
    const qs = location.state?.listSearch;
    navigate(`/apps/${routeAppId}/engage/funnel${qs ? `?${qs}` : ""}`);
  };

  const saveFunnel = async () => {
    if (!canManage || saving) return;
    if (!name.trim()) {
      toast.error("Enter a funnel name");
      document.getElementById("funnel-name")?.focus();
      return;
    }
    if (!canRun) {
      toast.error(duplicate ? "Each step must be a different event" : `Choose an event for every step (at least ${FUNNEL_MIN_STEPS})`);
      return;
    }
    setSaving(true);
    const res = await fetchWithAuthLogged("funnel", `${Utils.baseurl}save/funnel`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        app_id: appUuid,
        id: isNew ? undefined : funnelId,
        name: name.trim(),
        description: description.trim(),
        steps: cleanSteps,
        mode,
        window_days: windowDays || null,
        filters: filtersToSaved(filters),
      }),
    });
    const json = await res.json().catch(() => ({}));
    setSaving(false);
    if (handleSessionExpired(res, json)) return;
    if (!json.status) {
      toast.error(json.message || "Could not save the funnel");
      return;
    }
    toast.success(json.message || "Funnel saved");
    setSavedKey(currentKey);
    // show the report for exactly what was saved
    runWith(cleanSteps, mode, windowDays, filters, { silent: true });
    if (isNew && json.data?.id) {
      loadedFor.current = `${appUuid}:${json.data.id}`;
      navigate(`/apps/${routeAppId}/engage/funnel/${json.data.id}`, { replace: true, state: location.state });
    }
  };

  const runFunnel = () => {
    if (!canRun) {
      toast.error(duplicate ? "Each step must be a different event" : `Choose an event for every step (at least ${FUNNEL_MIN_STEPS})`);
      return;
    }
    runWith(cleanSteps, mode, windowDays, filters);
  };

  const onRefresh = () => {
    if (applied) runWith(applied.body.steps, applied.mode, applied.windowDays, applied.filters);
  };

  const loadUsers = useCallback(
    async (stepIndex, kind, page, group = "") => {
      if (!applied) return;
      setUsersLoading(true);
      let res;
      let json;
      try {
        res = await fetchWithAuthLogged("funnel", `${Utils.baseurl}get/campaign/funnel/users`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...applied.body, step_index: stepIndex, kind, page, pageSize: FUNNEL_USERS_PAGE_SIZE, ...(group ? { group } : {}) }),
        });
        json = await res.json().catch(() => ({}));
      } catch {
        toast.error("Could not reach the server. Please try again.");
        return;
      } finally {
        setUsersLoading(false);
      }
      if (handleSessionExpired(res, json)) return;
      if (!json.status) {
        toast.error(json.message || "Failed to load users");
        return;
      }
      setUsersData({ rows: json.data || [], meta: json.meta || {} });
    },
    [applied]
  );

  const openUsers = (stepIndex, kind, group = "") => {
    if (stepIndex < 0) return;
    // Opening a step lists the whole starting group (same count at every step); Progress shows how far each got.
    const listKind = kind === "reached" ? "all" : kind;
    setUsersModal({ show: true, stepIndex, kind: listKind, group });
    setUsersPage(1);
    setUsersData({ rows: [], meta: {} });
    loadUsers(stepIndex, listKind, 1, group);
  };
  const openSegmentModal = () => {
    const stepName = labelizeKey(applied?.body?.steps?.[usersModal.stepIndex]);
    const nextName = labelizeKey(applied?.body?.steps?.[usersModal.stepIndex + 1]);
    const base = name.trim() || "Funnel";
    setSegmentModal({
      show: true,
      saving: false,
      created: null,
      name: `${usersModal.kind === "dropped" ? `${base} · Dropped before ${nextName}` : usersModal.kind === "all" ? `${base} · All who started` : `${base} · Reached ${stepName}`}${usersModal.group ? ` · ${usersModal.group}` : ""}`,
    });
  };
  const createSegment = async () => {
    if (!applied || !segmentModal.name.trim()) {
      toast.error("Segment name is required");
      return;
    }
    setSegmentModal((m) => ({ ...m, saving: true }));
    let res;
    let json;
    try {
      res = await fetchWithAuthLogged("funnel", `${Utils.baseurl}create/campaign/funnel/segment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...applied.body, step_index: usersModal.stepIndex, kind: usersModal.kind, ...(usersModal.group ? { group: usersModal.group } : {}), name: segmentModal.name.trim() }),
      });
      json = await res.json().catch(() => ({}));
    } catch {
      toast.error("Could not reach the server. Please try again.");
      setSegmentModal((m) => ({ ...m, saving: false }));
      return;
    }
    if (handleSessionExpired(res, json)) return;
    if (!json.status) {
      toast.error(json.message || "Could not create the segment");
      setSegmentModal((m) => ({ ...m, saving: false }));
      return;
    }
    setSegmentModal((m) => ({ ...m, saving: false, created: json.data }));
  };
  const goUseSegment = (path, state) => {
    setSegmentModal((m) => ({ ...m, show: false }));
    setUsersModal((m) => ({ ...m, show: false }));
    navigate(`/apps/${routeAppId}/${path}`, state ? { state } : undefined);
  };
  const goUseAutomation = async () => {
    const seg = segmentModal.created;
    if (!seg) return;
    let res;
    let json;
    try {
      res = await fetchWithAuthLogged("funnel", `${Utils.baseurl}automations/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ app_id: routeAppId, name: `${seg.name} automation`.slice(0, 150), include_segment_ids: [seg.id] }),
      });
      json = await res.json().catch(() => ({}));
    } catch {
      toast.error("Could not reach the server. Please try again.");
      return;
    }
    if (handleSessionExpired(res, json)) return;
    if (!json.status) {
      toast.error(json.message || "Could not create the automation");
      return;
    }
    goUseSegment(`automations/${json.data.id}`);
  };
  const changePage = (p) => {
    setUsersPage(p);
    loadUsers(usersModal.stepIndex, usersModal.kind, p, usersModal.group);
  };

  const exportUsers = async () => {
    if (!applied) return;
    setExporting(true);
    const { status, message } = await exportListCsv(
      "get/campaign/funnel/users",
      { ...applied.body, step_index: usersModal.stepIndex, kind: usersModal.kind, ...(usersModal.group ? { group: usersModal.group } : {}) },
      `funnel-${applied.body.steps[usersModal.stepIndex].replace(/[^a-z0-9_-]+/gi, "-")}-${usersModal.kind}.csv`
    );
    setExporting(false);
    if (message) toast[status ? "info" : "error"](message);
  };

  const exportSummary = () => {
    if (!result?.data?.length) return;
    const withAmount = result.data.some((d) => d.amount != null);
    const withEvents = result.data.some((d) => d.events != null);
    const head = ["Step", "Event", "Users", ...(withEvents ? ["Events"] : []), "% of step 1", "% from previous", "Dropped", "Avg time from previous", ...(withAmount ? ["Amount"] : [])];
    const lines = result.data.map((d, i) => [
      i + 1,
      labelizeKey(d.event),
      d.users,
      ...(withEvents ? [d.events ?? ""] : []),
      d.pct_of_first,
      i === 0 ? "" : d.pct_of_prev,
      i === 0 ? "" : d.dropped,
      i === 0 ? "" : formatDuration(d.avg_ms_from_prev),
      ...(withAmount ? [d.amount ?? ""] : []),
    ]);
    const csv = [head, ...lines].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `funnel-${applied.body.steps.join("-").replace(/[^a-z0-9_-]+/gi, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const moveStep = (i, d) =>
    setSteps((s) => {
      const j = i + d;
      if (j < 0 || j >= s.length) return s;
      const n = [...s];
      [n[i], n[j]] = [n[j], n[i]];
      return n;
    });

  const onResetFilters = () => {
    setFilters((p) => ({ ...p, campaign_id: "", is_organic: "", country: "", platform: "", state: "", city: "" }));
  };

  const activeFilterCount = [filters.campaign_id, filters.is_organic, filters.country, filters.platform, filters.state, filters.city, filters.exclude_events].filter((v) => String(v || "") !== "").length;

  const data = useMemo(() => result?.data || [], [result]);
  const isStepTableAtEnd = useScrollEndDetection(stepTableRef, stepTableInnerRef, data.length);
  const isBreakdownAtEnd = useScrollEndDetection(breakdownTableRef, breakdownTableInnerRef, `${result?.breakdown?.by}:${result?.breakdown?.groups?.length}`);
  const summary = result?.summary;
  const appliedSteps = applied?.body?.steps || [];

  // Biggest leak between two consecutive steps — a marketer's first question.
  const biggestDrop = useMemo(() => {
    let best = null;
    data.forEach((d, i) => {
      if (i === 0 || !data[i - 1].users) return;
      const lost = 100 - (Number(d.pct_of_prev) || 0);
      if (!best || d.dropped > best.users || (d.dropped === best.users && lost > best.lost)) best = { i, lost, users: d.dropped };
    });
    return best;
  }, [data]);

  const appliedChips = applied
    ? [
        dateRangeLabel(applied.filters.datePreset, applied.filters.dateFrom, applied.filters.dateTo),
        FUNNEL_MODE_CODES.find((m) => m.key === applied.mode)?.label || "In order",
        FUNNEL_WINDOW_OPTIONS.find((o) => o.value === String(applied.windowDays || ""))?.label || "No time limit",
        applied.filters.is_organic !== "" ? FUNNEL_SOURCE_OPTIONS.find((o) => o.value === String(applied.filters.is_organic))?.label : null,
        applied.filters.campaign_id ? "1 campaign" : null,
        applied.filters.country ? `Country: ${applied.filters.country}` : null,
        applied.filters.platform ? `Platform: ${applied.filters.platform}` : null,
        applied.filters.state ? `State: ${applied.filters.state}` : null,
        applied.filters.city ? `City: ${applied.filters.city}` : null,
      ].filter(Boolean)
    : [];

  return (
    <div className="dashboard_right">
      <div className="dashboard_inner funnel-page">
        <div className="dashboard_right_top">
          <div className="dashboard_heading">
            <h1>
              {isNew ? "Create funnel" : name.trim() || "Funnel"}
              {!isNew && isDirty && <span className="badge rounded-pill bg-light-warning funnel-dirty-badge">Unsaved changes</span>}
            </h1>
          </div>
          <div className="add_app" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            {result && (
              <>
                <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh with latest data" onClick={onRefresh} disabled={loading}>
                  <FiRefreshCw />
                  <span>Refresh</span>
                </button>
                <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Download step summary as CSV" onClick={exportSummary}>
                  <MdFileDownload />
                  <span>Export</span>
                </button>
              </>
            )}
            {canManage && !funnelMissing && (
              <button
                type="button"
                className="add_app_btn btn_theme_primary"
                title={isNew ? "Save this funnel with its filters" : "Save changes to steps and filters"}
                onClick={saveFunnel}
                disabled={saving || funnelLoading || !appUuid || (!isNew && !isDirty)}
              >
                <MdSave />
                <span>{saving ? "Saving…" : isNew ? "Save funnel" : "Save changes"}</span>
              </button>
            )}
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" onClick={goBack}>
              <MdArrowBack />
              <span>Back</span>
            </button>
          </div>
        </div>

        {funnelMissing ? (
          <div className="funnel-card funnel-empty">
            <MdPeopleOutline size={38} />
            <h3>Funnel not found</h3>
            <p>It may have been deleted. Go back to the list to open another funnel.</p>
          </div>
        ) : funnelLoading ? (
          <div className="funnel-card">
            <SkeletonLoader columns={[1, 2, 3, 4]} rows={4} />
          </div>
        ) : (
        <>

        {/* ---------- name / description ---------- */}
        <div className="funnel-card">
          <div className="funnel-card-head">
            <div>
              <h2>Funnel details</h2>
            </div>
          </div>
          <div className="row g-3 funnel-meta">
            <div className="col-12 col-lg-4">
              <label htmlFor="funnel-name">Funnel name <span className="text-danger">*</span></label>
              <input
                id="funnel-name"
                className="form-control"
                placeholder="e.g. Install to first booking"
                maxLength={150}
                value={name}
                disabled={!canManage}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="col-12 col-lg-8">
              <label htmlFor="funnel-description">Description</label>
              <input
                id="funnel-description"
                className="form-control"
                placeholder="Optional — what this funnel tracks"
                maxLength={500}
                value={description}
                disabled={!canManage}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* ---------- builder ---------- */}
        <div className="funnel-card">
          <div className="funnel-card-head">
            <div>
              <h2>
                Funnel steps{" "}
                <HelpTooltip size={15} text="Choose the events in the order users should do them (for example: Install → Signup → Booking)." />
              </h2>
            </div>
          </div>
          <div className={`funnel-steps ${steps.length > 4 ? "funnel-steps--many" : ""}`}>
            {steps.map((s, i) => (
              <React.Fragment key={i}>
                <div className={`funnel-step ${!s ? "funnel-step--empty" : ""} ${s && isDupStep(s, i) ? "funnel-step--dup" : ""}`}>
                  <div className="funnel-step-top">
                    <span className="funnel-step-num">{i + 1}</span>
                    <span className="funnel-step-label">Step {i + 1}</span>
                    <div className="funnel-step-actions">
                      <button type="button" title="Move left" disabled={i === 0} onClick={() => moveStep(i, -1)}><MdArrowBack /></button>
                      <button type="button" title="Move right" disabled={i === steps.length - 1} onClick={() => moveStep(i, 1)}><MdArrowForward /></button>
                      <button type="button" title="Remove step" disabled={steps.length <= FUNNEL_MIN_STEPS} onClick={() => setSteps((arr) => arr.filter((_, k) => k !== i))}><MdClose /></button>
                    </div>
                  </div>
                  <AsyncSearchSelect
                    inputId={`funnel-step-${i}`}
                    instanceId={`funnel-step-${i}`}
                    classNamePrefix="ns-funnel-step"
                    placeholder="Select event(s)…"
                    enabled={Boolean(appUuid)}
                    fetchOptions={stepOptsFor(i)}
                    isMulti
                    isClearable={false}
                    value={splitStep(s)}
                    onChange={(opts) => setSteps((arr) => arr.map((x, k) => (k === i ? (opts || []).map((o) => o.value).join(FUNNEL_STEP_SEPARATOR) : x)))}
                    styles={selectStyles}
                  />
                </div>
                {i < steps.length - 1 && <div className="funnel-step-arrow" aria-hidden="true"><MdArrowForward /></div>}
              </React.Fragment>
            ))}
            {steps.length < FUNNEL_MAX_STEPS && (
              <button type="button" className="funnel-step-add" onClick={() => setSteps((arr) => [...arr, ""])}>
                <MdAdd size={20} />
                <span>Add step</span>
              </button>
            )}
          </div>
          {duplicate && <div className="funnel-error">An event can be used in only one step.</div>}

          <div className="funnel-settings">
            <div className="funnel-setting">
              <label>
                Step order
                <HelpTooltip size={15} text="In order: a user counts for a step only if they did the earlier steps first (Install, then Signup, then Booking). Strict: like In order, but the next step must follow the previous one with nothing else in between; any other event in between stops the user there. Any order: a user counts once they have done all the steps up to that one, in any order." />
              </label>
              <div className="funnel-seg funnel-seg--inline">
                {FUNNEL_MODE_CODES.map((m) => (
                  <button key={m.key} type="button" className={mode === m.key ? "active" : ""} onClick={() => setMode(m.key)}>{m.label}</button>
                ))}
              </div>
            </div>
            <div className="funnel-setting">
              <label>
                Complete within
                <HelpTooltip size={15} text="How long a user has, counted from their first step, to finish the rest. The date range only decides which users entered (their first step); later steps can happen after the range ends as long as they are within this time." />
              </label>
              <Select
                inputId="funnel-window"
                instanceId="funnel-window"
                classNamePrefix="ns-funnel-window"
                options={FUNNEL_WINDOW_OPTIONS}
                value={FUNNEL_WINDOW_OPTIONS.find((o) => o.value === windowDays) || FUNNEL_WINDOW_OPTIONS[0]}
                onChange={(o) => setWindowDays(o?.value || "")}
                styles={selectStyles}
                isSearchable={false}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
              />
            </div>
            <div className="funnel-setting">
              <label>
                Break down by
                <HelpTooltip size={15} text="Shows the funnel side by side for each Platform, Campaign, Source, Country, State or City, so you can compare them." />
              </label>
              <Select
                inputId="funnel-breakdown"
                instanceId="funnel-breakdown"
                classNamePrefix="ns-funnel-breakdown"
                options={FUNNEL_BREAKDOWN_OPTIONS}
                value={FUNNEL_BREAKDOWN_OPTIONS.find((o) => o.value === (filters.breakdown || "")) || FUNNEL_BREAKDOWN_OPTIONS[0]}
                onChange={(o) => setFilters((p) => ({ ...p, breakdown: o?.value || "" }))}
                styles={selectStyles}
                isSearchable={false}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
              />
            </div>
            <div className="funnel-setting">
              <DateRangePicker
                dateFrom={filters.dateFrom}
                dateTo={filters.dateTo}
                preset={filters.datePreset}
                presets={DATE_RANGE_PRESETS}
                allowClear={false}
                onChange={({ dateFrom, dateTo, preset }) =>
                  setFilters((p) => ({ ...p, dateFrom: dateFrom || "", dateTo: dateTo || "", datePreset: preset || "" }))
                }
              />
            </div>
          </div>

          <div className="funnel-actions-row">
            <button type="button" className="funnel-more-toggle" onClick={() => setFiltersOpen((o) => !o)} aria-expanded={filtersOpen}>
              <MdFilterAlt />
              <span>More filters{activeFilterCount ? ` (${activeFilterCount})` : ""}</span>
              <MdKeyboardArrowDown className={filtersOpen ? "open" : ""} />
            </button>
            <div className="funnel-setting--actions">
              <button type="button" className="add_app_btn btn_theme_primary" onClick={runFunnel} disabled={loading || !appUuid}>
                <MdSearch />
                <span>{loading ? "Building…" : "Show funnel"}</span>
              </button>
              <button type="button" className="add_app_btn_secondary btn_theme_secondary" onClick={onResetFilters} disabled={loading}>
                <MdRefresh />
                <span>Reset filters</span>
              </button>
            </div>
          </div>
          <Collapse in={filtersOpen}>
            <div>
              <div className="row g-3 funnel-more">
                <div className="col-12 col-md-6 col-lg-3">
                  <label>Campaign</label>
                  <AsyncSearchSelect
                    inputId="funnel-campaign"
                    instanceId="funnel-campaign"
                    classNamePrefix="ns-funnel-campaign"
                    isClearable
                    placeholder="All campaigns"
                    enabled={Boolean(appUuid)}
                    fetchOptions={fetchCampaignOpts}
                    value={filters.campaign_id || null}
                    onChange={(opt) => setFilters((p) => ({ ...p, campaign_id: opt?.value ?? "" }))}
                    styles={selectStyles}
                  />
                </div>
                <div className="col-12 col-md-6 col-lg-3">
                  <label>Source</label>
                  <Select
                    inputId="funnel-source"
                    instanceId="funnel-source"
                    classNamePrefix="ns-funnel-source"
                    options={FUNNEL_SOURCE_OPTIONS}
                    isSearchable={false}
                    value={FUNNEL_SOURCE_OPTIONS.find((o) => o.value === String(filters.is_organic)) || FUNNEL_SOURCE_OPTIONS[0]}
                    onChange={(o) => setFilters((p) => ({ ...p, is_organic: o?.value ?? "" }))}
                    styles={selectStyles}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    menuPosition="fixed"
                  />
                </div>
                <div className="col-12 col-md-6 col-lg-3">
                  <label>Country</label>
                  <AsyncSearchSelect
                    inputId="funnel-country"
                    instanceId="funnel-country"
                    classNamePrefix="ns-funnel-country"
                    isClearable
                    placeholder="All countries"
                    enabled={Boolean(appUuid)}
                    fetchOptions={fetchCountryOpts}
                    value={filters.country || null}
                    onChange={(opt) => setFilters((p) => ({ ...p, country: opt?.value ?? "" }))}
                    styles={selectStyles}
                  />
                </div>
                <div className="col-12 col-md-6 col-lg-3">
                  <label>Platform</label>
                  <AsyncSearchSelect
                    inputId="funnel-platform"
                    instanceId="funnel-platform"
                    classNamePrefix="ns-funnel-platform"
                    isClearable
                    placeholder="All platforms"
                    enabled={Boolean(appUuid)}
                    fetchOptions={fetchPlatformOpts}
                    value={filters.platform || null}
                    onChange={(opt) => setFilters((p) => ({ ...p, platform: opt?.value ?? "" }))}
                    styles={selectStyles}
                  />
                </div>
                <div className="col-12 col-md-6 col-lg-3">
                  <label>
                    State
                    <HelpTooltip size={15} text="Only users whose profile location is in this state. Anonymous users (not logged in yet) have no location and are left out." />
                  </label>
                  <AsyncSearchSelect
                    inputId="funnel-state"
                    instanceId="funnel-state"
                    classNamePrefix="ns-funnel-state"
                    isClearable
                    placeholder="All states"
                    enabled={Boolean(appUuid)}
                    fetchOptions={fetchStateOpts}
                    value={filters.state || null}
                    onChange={(opt) => setFilters((p) => ({ ...p, state: opt?.value ?? "" }))}
                    styles={selectStyles}
                  />
                </div>
                <div className="col-12 col-md-6 col-lg-3">
                  <label>City</label>
                  <AsyncSearchSelect
                    inputId="funnel-city"
                    instanceId="funnel-city"
                    classNamePrefix="ns-funnel-city"
                    isClearable
                    placeholder="All cities"
                    enabled={Boolean(appUuid)}
                    fetchOptions={fetchCityOpts}
                    value={filters.city || null}
                    onChange={(opt) => setFilters((p) => ({ ...p, city: opt?.value ?? "" }))}
                    styles={selectStyles}
                  />
                </div>
                <div className="col-12 col-md-6 col-lg-6">
                  <label>
                    Exclude users who did
                    <HelpTooltip size={15} text="Users who did any of these events after their first step are left out of the funnel completely, so they count neither as converted nor as dropped (for example Uninstall)." />
                  </label>
                  <AsyncSearchSelect
                    inputId="funnel-exclude"
                    instanceId="funnel-exclude"
                    classNamePrefix="ns-funnel-exclude"
                    isMulti
                    isClearable
                    placeholder="No exclusions"
                    enabled={Boolean(appUuid)}
                    fetchOptions={fetchExcludeOpts}
                    value={splitStep(String(filters.exclude_events || "").split(",").join(FUNNEL_STEP_SEPARATOR))}
                    onChange={(opts) => setFilters((p) => ({ ...p, exclude_events: (opts || []).map((o) => o.value).join(",") }))}
                    styles={selectStyles}
                  />
                </div>
              </div>
            </div>
          </Collapse>
        </div>

        {/* ---------- results ---------- */}
        {loading && !result && (
          <div className="funnel-card">
            <div className="app-dashboard__chart-skeleton skeleton-cell" aria-hidden="true" />
          </div>
        )}

        {!result && !loading && (
          <div className="funnel-card funnel-empty">
            <div className="funnel-empty-art" aria-hidden="true">
              <span style={{ width: "100%" }} />
              <span style={{ width: "70%" }} />
              <span style={{ width: "42%" }} />
            </div>
            <h3>Build your first funnel</h3>
            <p>Pick at least two events above and click <strong>Show funnel</strong>. Then <strong>Save funnel</strong> to keep it with its filters in the funnel list.</p>
          </div>
        )}

        {result && (
          <div className={`funnel-results ${loading ? "is-loading" : ""}`}>
            <div className="funnel-applied">
              <span className="funnel-applied-label">Results for</span>
              <span className="funnel-applied-path">
                {appliedSteps.map((s, i) => (
                  <React.Fragment key={s}>
                    {i > 0 && <MdArrowForward />}
                    <strong>{labelizeKey(s)}</strong>
                  </React.Fragment>
                ))}
              </span>
              {appliedChips.map((c) => (
                <span key={c} className="badge rounded-pill bg-light-secondary">{c}</span>
              ))}
            </div>

            {summary.truncated && (
              <div className="alert alert-warning py-2">
                Very large date range — figures use the first 500,000 events. Pick a shorter range for exact numbers.
              </div>
            )}

            <div className="campaign-summary-row">
              <div className="campaign-summary-card">
                <span className="campaign-summary-label">
                  Started ({labelizeKey(appliedSteps[0])}) <HelpTooltip size={15} text={KPI_HELP.started} />
                </span>
                <strong className="campaign-summary-value">{bigAmountFormat(summary.entered)}</strong>
              </div>
              <div className="campaign-summary-card">
                <span className="campaign-summary-label">
                  Completed ({labelizeKey(appliedSteps[appliedSteps.length - 1])}) <HelpTooltip size={15} text={KPI_HELP.completed} />
                </span>
                <strong className="campaign-summary-value">{bigAmountFormat(summary.completed)}</strong>
                {data[data.length - 1]?.amount != null && (
                  <div className="funnel-kpi-sub">{money(data[data.length - 1].amount)}{data[data.length - 1].events != null ? ` (${bigAmountFormat(data[data.length - 1].events)})` : ""} total</div>
                )}
              </div>
              <div className="campaign-summary-card">
                <span className="campaign-summary-label">
                  Overall conversion <HelpTooltip size={15} text={KPI_HELP.conversion} />
                </span>
                <strong className="campaign-summary-value">{pct(summary.overall_conversion)}</strong>
                <div className="funnel-meter-bar"><i style={{ width: `${Math.min(100, summary.overall_conversion)}%` }} /></div>
              </div>
              <div className="campaign-summary-card">
                <span className="campaign-summary-label">
                  Biggest drop-off <HelpTooltip size={15} text={KPI_HELP.drop} />
                </span>
                {biggestDrop ? (
                  <>
                    <strong className="campaign-summary-value">{pct(biggestDrop.lost)}</strong>
                    <small className="text-muted">
                      {labelizeKey(data[biggestDrop.i - 1].event)} → {labelizeKey(data[biggestDrop.i].event)} · {plural(biggestDrop.users, "user")}
                    </small>
                  </>
                ) : (
                  <strong className="campaign-summary-value">—</strong>
                )}
              </div>
              <div className="campaign-summary-card">
                <span className="campaign-summary-label">
                  {applied?.mode === "any" ? "Avg span to complete" : "Avg time to complete"} <HelpTooltip size={15} text={KPI_HELP.time} />
                </span>
                <strong className="campaign-summary-value">{formatDuration(summary.avg_ms_total)}</strong>
                {summary.median_ms_total != null && <div className="funnel-kpi-sub">Median {formatDuration(summary.median_ms_total)}</div>}
              </div>
            </div>

            {summary.entered === 0 ? (
              <div className="funnel-card funnel-empty">
                <MdPeopleOutline size={38} />
                <h3>No users did {labelizeKey(appliedSteps[0])} in this period</h3>
                <p>Try a longer date range or remove some filters.</p>
              </div>
            ) : (
              <>
                <div className="funnel-insight">
                  <strong>{plural(summary.entered, "user")}</strong> did {labelizeKey(appliedSteps[0])}.{" "}
                  <strong>{bigAmountFormat(summary.completed)}</strong> ({pct(summary.overall_conversion)}) went all the way to{" "}
                  {labelizeKey(appliedSteps[appliedSteps.length - 1])}.
                  {applied?.filters?.datePreset && applied.filters.datePreset !== "all_time" && summary.entered <= 1 && (
                    <>
                      {" "}Only a few users in this period. Try a longer date range to see more.
                    </>
                  )}
                  {biggestDrop && biggestDrop.users > 0 && (
                    <>
                      {" "}The most users are lost between <strong>{labelizeKey(data[biggestDrop.i - 1].event)}</strong> and{" "}
                      <strong>{labelizeKey(data[biggestDrop.i].event)}</strong> ({plural(biggestDrop.users, "user")}, {pct(biggestDrop.lost)}).
                    </>
                  )}
                </div>

                <div className="funnel-card">
                  <div className="funnel-card-head">
                    <div>
                      <h2>Conversion funnel</h2>
                      <p>Bar height = share of users from step 1 who reached each step.</p>
                    </div>
                  </div>
                  <FunnelChart data={data} onOpen={openUsers} />
                </div>

                <div className="funnel-card">
                  <div className="funnel-card-head">
                    <div>
                      <h2>Step breakdown</h2>
                      <p>Open any step to see exactly which users reached it or dropped off.</p>
                    </div>
                  </div>
                  <div className="dashboard_app_table dashboard_app_table--list-records">
                   <div className="dashboard_table">
                    <div ref={stepTableRef} className="table_scroll_wrapper">
                     <div ref={stepTableInnerRef} className={`table_inner ${isStepTableAtEnd ? "at-scroll-end" : ""}`} style={{ overflowX: "auto" }}>
                    <table className="table_outer table-sm">
                      <thead>
                        <tr>
                          <th>Step</th>
                          <th className="text-end">Users</th>
                          {data.some((x) => x.events != null) && <th className="text-end">Events</th>}
                          <th style={{ minWidth: 180 }}>Of step 1</th>
                          <th className="text-end">From previous</th>
                          <th className="text-end">Lost from previous</th>
                          <th className="text-end">Avg time from previous</th>
                          <th className="text-end">Median time</th>
                          {data.some((x) => x.amount != null) && <th className="text-end">Amount</th>}
                          <th className="col-action" style={{ textAlign: "center" }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.map((d, i) => {
                          return (
                            <tr key={d.event}>
                              <td>
                                <div className="funnel-table-step">
                                  <span className="funnel-step-num">{i + 1}</span>
                                  <strong>{labelizeKey(d.event)}</strong>
                                </div>
                              </td>
                              <td className="text-end"><strong>{bigAmountFormat(d.users)}</strong></td>
                              {data.some((x) => x.events != null) && <td className="text-end">{d.events != null ? bigAmountFormat(d.events) : "—"}</td>}
                              <td>
                                <div className="funnel-meter">
                                  <div className="funnel-meter-bar"><i style={{ width: `${d.pct_of_first}%` }} /></div>
                                  <span>{pct(d.pct_of_first)}</span>
                                </div>
                              </td>
                              <td className="text-end">{i === 0 ? "—" : pct(d.pct_of_prev)}</td>
                              <td className="text-end">{i === 0 ? "—" : d.dropped ? `−${bigAmountFormat(d.dropped)}` : "0"}</td>
                              <td className="text-end">{i === 0 || d.avg_ms_from_prev == null ? "—" : formatDuration(d.avg_ms_from_prev)}</td>
                              <td className="text-end">{i === 0 || d.median_ms_from_prev == null ? "—" : formatDuration(d.median_ms_from_prev)}</td>
                              {data.some((x) => x.amount != null) && (
                                <td className="text-end">{d.amount != null ? <strong>{money(d.amount)}{d.events != null ? ` (${bigAmountFormat(d.events)})` : ""}</strong> : "—"}</td>
                              )}
                              <td className="col-action" style={{ textAlign: "center" }}>
                                <button
                                  type="button"
                                  className="dropdown-trigger-btn btn btn-link p-0"
                                  aria-label="Actions"
                                  aria-expanded={openActionId === d.event}
                                  onClick={(e) => handleOpenDropdown(e, d.event, { i, users: d.users })}
                                >
                                  <BsThreeDotsVertical />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                     </div>
                    </div>
                   </div>
                  </div>
                </div>
                {result.trend?.points?.length > 1 && (
                  <div className="funnel-card">
                    <div className="funnel-card-head">
                      <div>
                        <h2>Conversion trend</h2>
                        <p>
                          Users grouped by the {result.trend.unit} they did {labelizeKey(appliedSteps[0])}. Bars are how many started,
                          the orange bar (right-hand % scale) is the share of them who completed {labelizeKey(appliedSteps[appliedSteps.length - 1])}.
                        </p>
                      </div>
                    </div>
                    <div style={{ width: "100%", height: 280 }}>
                      <ResponsiveContainer>
                        <ComposedChart data={result.trend.points} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => trendLabel(d, result.trend.unit)} />
                          <YAxis yAxisId="n" allowDecimals={false} tick={{ fontSize: 11 }} width={36} />
                          <YAxis yAxisId="p" orientation="right" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} width={44} />
                          <Tooltip
                            formatter={(v, n) => (n === "Conversion" ? (v == null ? "—" : `${v}%`) : v)}
                            labelFormatter={(d) => `${result.trend.unit === "week" ? "Week of " : ""}${trendLabel(d, result.trend.unit, true)}`}
                          />
                          <Bar yAxisId="n" dataKey="started" name="Started" fill="#c7cbf5" radius={[4, 4, 0, 0]} />
                          <Bar yAxisId="n" dataKey="completed" name="Completed" fill="#4545c4" radius={[4, 4, 0, 0]} />
                          <Bar yAxisId="p" dataKey="rate" name="Conversion" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
                {result.breakdown?.groups?.length > 0 && (
                  <div className="funnel-card">
                    <div className="funnel-card-head">
                      <div>
                        <h2>Breakdown by {FUNNEL_BREAKDOWN_OPTIONS.find((o) => o.value === result.breakdown.by)?.label || result.breakdown.by}</h2>
                        <p>The same funnel, split by group. Each cell is the users who reached that step and their share of the group's first step.</p>
                      </div>
                    </div>
                    <div className="dashboard_app_table dashboard_app_table--list-records">
                      <div className="dashboard_table">
                        <div ref={breakdownTableRef} className="table_scroll_wrapper">
                          <div ref={breakdownTableInnerRef} className={`table_inner ${isBreakdownAtEnd ? "at-scroll-end" : ""}`} style={{ overflowX: "auto" }}>
                            <table className="table_outer table-sm">
                              <thead>
                                <tr>
                                  <th>{FUNNEL_BREAKDOWN_OPTIONS.find((o) => o.value === result.breakdown.by)?.label || "Group"}</th>
                                  {appliedSteps.map((s, i) => (
                                    <th key={s} className="text-end">{i + 1}. {labelizeKey(s)}</th>
                                  ))}
                                  <th className="text-end">Overall conversion</th>
                                  <th className="col-action" style={{ textAlign: "center" }}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {result.breakdown.groups.map((g) => (
                                  <tr key={g.label}>
                                    <td><strong>{g.label}</strong></td>
                                    {g.users.map((n, i) => (
                                      <td key={i} className="text-end">
                                        <strong>{bigAmountFormat(n)}</strong>
                                        {i > 0 && <small className="text-muted"> · {pct(g.pct_of_first[i])}</small>}
                                      </td>
                                    ))}
                                    <td className="text-end"><strong>{pct(g.pct_of_first[g.pct_of_first.length - 1])}</strong></td>
                                    <td className="col-action" style={{ textAlign: "center" }}>
                                      <button
                                        type="button"
                                        className="dropdown-trigger-btn btn btn-link p-0"
                                        aria-label="Actions"
                                        aria-expanded={openActionId === `bd:${g.label}`}
                                        onClick={(e) => handleOpenDropdown(e, `bd:${g.label}`, { i: appliedSteps.length - 1, users: g.users[0], group: g.label })}
                                      >
                                        <BsThreeDotsVertical />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        </>
        )}

        {openActionItem && dropdownPosition
          ? createPortal(
              <div ref={dropdownRef} style={{ position: "fixed", left: dropdownPosition.left, top: dropdownPosition.top, zIndex: 999999, minWidth: "200px" }}>
                <div className="dropdown-menu-subscription">
                  <button
                    type="button"
                    disabled={!openActionItem.users}
                    onClick={() => handleDropdownAction(() => openUsers(openActionItem.i, "reached", openActionItem.group || ""))}
                    style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                  >
                    <FiExternalLink size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                    <span style={{ fontSize: "0.875rem" }}>View users</span>
                  </button>
                </div>
              </div>,
              document.body
            )
          : null}
        <FunnelUsersModal
          mode={applied?.mode}
          show={usersModal.show}
          onHide={() => setUsersModal((m) => ({ ...m, show: false }))}
          stepIndex={usersModal.stepIndex}
          kind={usersModal.kind}
          group={usersModal.group}
          steps={appliedSteps}
          data={data}
          rows={usersData.rows}
          meta={usersData.meta}
          loading={usersLoading}
          page={usersPage}
          onPage={changePage}
          onExport={exportUsers}
          exporting={exporting}
          onCreateSegment={can("segments.add") ? openSegmentModal : null}
        />

        <Modal show={segmentModal.show} onHide={() => setSegmentModal((m) => ({ ...m, show: false }))} className="ns-modal" centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {segmentModal.created ? "Segment ready" : "Message these users"}
              <small className="funnel-users-sub">
                {segmentModal.created
                  ? "Pick where to use it"
                  : "Saves these users as a segment, which Push, WhatsApp and Automations can use as their audience."}
              </small>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {segmentModal.created ? (
              <>
                <p className="mb-2">
                  <strong>{segmentModal.created.name}</strong> has {plural(segmentModal.created.users, "user")} ({plural(segmentModal.created.subscriptions, "reachable device")}).
                </p>
                {segmentModal.created.reused && (
                  <div className="alert alert-info py-2">These exact users were already saved, so the existing segment is used (no duplicate created).</div>
                )}
                {segmentModal.created.capped && (
                  <div className="alert alert-warning py-2">Only the {bigAmountFormat(segmentModal.created.users)} newest of {bigAmountFormat(segmentModal.created.users_total)} users were added (segment limit).</div>
                )}
                {segmentModal.created.anonymous_skipped > 0 && (
                  <div className="alert alert-light border py-2">{plural(segmentModal.created.anonymous_skipped, "anonymous user")} left out — they are not logged in, so a segment cannot address them.</div>
                )}
                <div className="funnel-segment-actions">
                  {can("push.view") && (
                    <button type="button" className="btn_theme_primary" onClick={() => goUseSegment("push-notification", { presetSegmentIds: [segmentModal.created.id] })}>Send push</button>
                  )}
                  {can("whatsapp.send.view") && (
                    <button type="button" className="btn_theme_primary" onClick={() => goUseSegment("whatsapp/bulk", { presetSegmentIds: [segmentModal.created.id] })}>Send WhatsApp</button>
                  )}
                  {can("automation.view") && (
                    <button type="button" className="btn_theme_secondary" onClick={goUseAutomation}>Use in an Automation</button>
                  )}
                  <button type="button" className="btn_theme_secondary" onClick={() => goUseSegment("segments")}>View segments</button>
                </div>
              </>
            ) : (
              <>
                <label className="form-label">Segment name</label>
                <input
                  type="text"
                  className="form-control"
                  value={segmentModal.name}
                  maxLength={150}
                  onChange={(e) => setSegmentModal((m) => ({ ...m, name: e.target.value }))}
                  autoFocus
                />
                <small className="text-muted d-block mt-2">
                  A fixed list of the {usersModal.kind === "dropped" ? "dropped" : usersModal.kind === "all" ? "starting" : "reached"} users right now (logged-in users only, up to {FUNNEL_SEGMENT_USER_CAP.toLocaleString("en-IN")}).
                </small>
              </>
            )}
          </Modal.Body>
          {!segmentModal.created && (
            <Modal.Footer>
              <button type="button" className="btn_theme_secondary" onClick={() => setSegmentModal((m) => ({ ...m, show: false }))}>Cancel</button>
              <button type="button" className="btn_theme_primary" onClick={createSegment} disabled={segmentModal.saving}>
                {segmentModal.saving ? "Creating…" : "Create segment"}
              </button>
            </Modal.Footer>
          )}
        </Modal>
      </div>
    </div>
  );
}
